import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { RATE_META, fmtDate, fmtIDR } from "@/lib_app/api";
import Avatar from "@/components/app/Avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Bell,
  CheckCheck,
  Check,
  MessageSquareText,
  AlertTriangle,
  BellRing,
  BellOff,
  Volume2,
  VolumeX,
  History,
  Inbox,
  RotateCcw,
  Timer,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";
import NotificationQuickView from "@/components/app/NotificationQuickView";

// Simple pleasant beep via WebAudio (no external asset needed)
function playPing() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.15);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.15, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.4);
    setTimeout(() => ctx.close(), 500);
  } catch {}
}

// Poll faster when tab is visible so notifications feel near real-time
const POLL_ACTIVE_MS = 15_000;
const POLL_HIDDEN_MS = 60_000;

export default function NotificationBell() {
  const [data, setData] = useState({ notes: [], overdue: [], overdue_projects: [], unread_notes: 0, overdue_count: 0, overdue_projects_count: 0, total_count: 0, read_at: "" });
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("sp_sound_notif") !== "0");
  const [tab, setTab] = useState("unread"); // "unread" | "history"
  const [quickView, setQuickView] = useState(null); // { prospectId, replyTo, focusReply, noteId }
  const seenIdsRef = useRef(null);
  const firstLoadRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("sp_sound_notif", soundOn ? "1" : "0"); }, [soundOn]);

  const showBrowserNotif = (n) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      const notif = new Notification(`💬 ${n.actor_name} · ${n.customer}`, {
        body: n.note?.slice(0, 160) || "",
        icon: "/favicon.ico",
        tag: `note-${n.id}`,
        renotify: true,
      });
      notif.onclick = () => {
        window.focus();
        setQuickView({ prospectId: n.prospect_id, replyTo: n.actor_id, focusReply: true });
        notif.close();
      };
    } catch { /* noop */ }
  };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications");
      setData(d);
      // Only "unread" notes should ping
      const unreadIds = new Set((d.notes || []).filter((n) => !n.read).map((n) => n.id));
      if (firstLoadRef.current) {
        seenIdsRef.current = unreadIds;
        firstLoadRef.current = false;
        return;
      }
      const brandNew = (d.notes || []).filter((n) => !n.read && !seenIdsRef.current.has(n.id));
      if (brandNew.length > 0 && soundOn) playPing();
      brandNew.forEach((n) => {
        toast.info(`💬 ${n.actor_name}: ${n.note.slice(0, 80)}${n.note.length > 80 ? "…" : ""}`, {
          description: `Prospek: ${n.customer}`,
          duration: 6000,
        });
        showBrowserNotif(n);
      });
      seenIdsRef.current = unreadIds;
    } catch { /* noop */ }
  }, [navigate, soundOn]);

  useEffect(() => {
    load();
    let interval = setInterval(load, document.visibilityState === "visible" ? POLL_ACTIVE_MS : POLL_HIDDEN_MS);
    const onVis = () => {
      clearInterval(interval);
      interval = setInterval(load, document.visibilityState === "visible" ? POLL_ACTIVE_MS : POLL_HIDDEN_MS);
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [load]);

  const enablePush = async () => {
    if (typeof Notification === "undefined") return toast.error("Browser tidak mendukung notifikasi");
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") toast.success("Notifikasi real-time aktif");
      else toast.error("Notifikasi ditolak");
    } catch {
      toast.error("Gagal aktifkan notifikasi");
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read_all");
      load();
      toast.success("Semua ditandai dibaca");
    } catch {
      toast.error("Gagal");
    }
  };

  const markNoteRead = async (e, noteId) => {
    e.stopPropagation();
    // Optimistic update
    setData((prev) => ({
      ...prev,
      notes: (prev.notes || []).map((n) => (n.id === noteId ? { ...n, read: true } : n)),
      unread_notes: Math.max(0, (prev.unread_notes || 0) - 1),
      total_count: Math.max(0, (prev.total_count || 0) - 1),
    }));
    try {
      await api.post(`/notifications/notes/${noteId}/read`);
    } catch {
      toast.error("Gagal menandai dibaca");
      load();
    }
  };

  const openQuickForNote = (n) => {
    // Auto mark-as-read when opening
    if (!n.read) markNoteRead({ stopPropagation: () => {} }, n.id);
    setQuickView({ prospectId: n.prospect_id, replyTo: n.actor_id, focusReply: true, noteId: n.id });
  };

  const openQuickForOverdue = (o) => {
    // Consistent with Dashboard's Perlu Follow-up: navigate to the prospect edit modal
    navigate(`/prospects?open=${o.id}`);
  };

  const markNoteUnread = async (e, noteId) => {
    e.stopPropagation();
    setData((prev) => ({
      ...prev,
      notes: (prev.notes || []).map((n) => (n.id === noteId ? { ...n, read: false } : n)),
      unread_notes: (prev.unread_notes || 0) + 1,
      total_count: (prev.total_count || 0) + 1,
    }));
    try {
      await api.post(`/notifications/notes/${noteId}/unread`);
    } catch {
      toast.error("Gagal");
      load();
    }
  };

  const count = data.total_count || 0;
  const notes = data.notes || [];
  const unreadNotes = notes.filter((n) => !n.read);
  const historyNotes = notes.filter((n) => n.read);

  return (
    <>
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label="Notifikasi"
          className="relative w-11 h-11 rounded-full bg-white flex items-center justify-center text-[#14161F] transition-all shadow-[0_2px_8px_rgba(20,22,31,0.06)] hover:shadow-[0_4px_14px_rgba(20,22,31,0.10)]"
        >
          <Bell className="w-[18px] h-[18px]" strokeWidth={1.5} />
          {count > 0 && (
            <span
              data-testid="notification-count"
              className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white ${
                data.unread_notes > 0 ? "bg-[#FF6B7D] animate-pulse" : "bg-[#FFDB52]"
              }`}
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[420px] p-0 rounded-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="notification-popover">
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between shrink-0">
          <div>
            <div className="text-sm font-bold text-[#14161F] font-display">Notifikasi</div>
            <div className="text-xs text-[#626061] mt-0.5">
              {tab === "unread" ? `${count} item baru` : `${historyNotes.length} tersimpan`}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setSoundOn((v) => { const nv = !v; if (nv) playPing(); return nv; }); }}
              data-testid="sound-toggle-btn"
              title={soundOn ? "Matikan suara" : "Aktifkan suara"}
              className={`w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors ${
                soundOn ? "bg-[#14161F] text-white hover:bg-black" : "bg-black/5 text-[#626061] hover:bg-black/10"
              }`}
            >
              {soundOn ? <Volume2 className="w-3.5 h-3.5" strokeWidth={1.5} /> : <VolumeX className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
            {tab === "unread" && (
              <button
                onClick={markAllRead}
                disabled={data.unread_notes === 0}
                data-testid="mark-all-read-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-black/5 hover:bg-black/10 text-[#14161F] text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Tandai semua
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-3 pt-3 pb-2 flex items-center gap-1 border-b border-black/5 shrink-0" data-testid="notif-tabs">
          <button
            onClick={() => setTab("unread")}
            data-testid="notif-tab-unread"
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${
              tab === "unread"
                ? "bg-[#14161F] text-white"
                : "bg-transparent text-[#626061] hover:bg-black/5"
            }`}
          >
            <Inbox className="w-3.5 h-3.5" strokeWidth={1.5} /> Belum Dibaca
            {(unreadNotes.length + (data.overdue?.length || 0) + (data.overdue_projects?.length || 0)) > 0 && (
              <span className={`ml-1 min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                tab === "unread" ? "bg-white/25 text-white" : "bg-[#14161F] text-white"
              }`}>
                {unreadNotes.length + (data.overdue?.length || 0) + (data.overdue_projects?.length || 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("history")}
            data-testid="notif-tab-history"
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${
              tab === "history"
                ? "bg-[#14161F] text-white"
                : "bg-transparent text-[#626061] hover:bg-black/5"
            }`}
          >
            <History className="w-3.5 h-3.5" strokeWidth={1.5} /> Riwayat
            {historyNotes.length > 0 && (
              <span className={`ml-1 min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                tab === "history" ? "bg-white/25 text-white" : "bg-black/10 text-[#626061]"
              }`}>
                {historyNotes.length}
              </span>
            )}
          </button>
        </div>

        {/* Push notification permission banner (only on unread tab) */}
        {tab === "unread" && permission !== "granted" && permission !== "unsupported" && (
          <div className="px-5 py-3 bg-[#F4F5F7] border-b border-[#DCE0E7] flex items-center gap-3">
            {permission === "denied" ? (
              <BellOff className="w-4 h-4 text-[#626061] shrink-0" />
            ) : (
              <BellRing className="w-4 h-4 text-[#090808] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#090808]">
                {permission === "denied" ? "Notifikasi diblokir" : "Aktifkan notifikasi real-time"}
              </div>
              <div className="text-[11px] text-[#A0A3A8] mt-0.5">
                {permission === "denied"
                  ? "Buka pengaturan browser untuk mengizinkan."
                  : "Dapatkan ping instan saat tim memberi catatan."}
              </div>
            </div>
            {permission !== "denied" && (
              <button
                onClick={enablePush}
                data-testid="enable-push-btn"
                className="h-7 px-3 rounded-full bg-[#090808] hover:bg-[#000000] text-white text-[11px] font-semibold shrink-0"
              >
                Aktifkan
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto flex-1" data-testid={`notif-panel-${tab}`}>
          {tab === "unread" ? (
            <>
              <Section
                icon={MessageSquareText}
                tint="text-[#4E6D9A] bg-[#DCE0E7]"
                title="Catatan Tim"
                empty="Belum ada catatan baru dari tim."
                onMore={() => navigate("/notifications?type=note")}
                items={unreadNotes}
                renderItem={(n) => (
                  <NoteRow
                    key={n.id}
                    n={n}
                    unread
                    onOpen={() => openQuickForNote(n)}
                    onMarkRead={(e) => markNoteRead(e, n.id)}
                  />
                )}
              />

              <Section
                icon={AlertTriangle}
                tint="text-[#B39548] bg-[#FFE89A]"
                title="Perlu Follow-up (H+7)"
                empty="Semua prospek terkini. 🎉"
                onMore={() => navigate("/notifications?type=prospect_overdue")}
                items={data.overdue || []}
                renderItem={(o) => {
                  const m = RATE_META[o.rate] || RATE_META[3];
                  return (
                    <button
                      key={o.id}
                      onClick={() => openQuickForOverdue(o)}
                      data-testid={`notif-overdue-${o.id}`}
                      className="w-full text-left px-5 py-3 hover:bg-[#F4F5F7] transition-colors border-b border-[#DCE0E7] last:border-0 flex items-center gap-3"
                    >
                      <div className={`w-8 h-8 rounded-full ${m.pill} text-white font-bold flex items-center justify-center text-xs shrink-0`}>
                        {m.letter}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[#090808] truncate flex items-baseline gap-1.5">
                          <span className="font-semibold">{o.customer}</span>
                          {o.service && <span className="text-[11px] text-[#A0A3A8] font-normal truncate">· {o.service}</span>}
                        </div>
                        <div className="text-xs text-[#A0A3A8] truncate">{o.owner_name}</div>
                      </div>
                      <span className={`text-xs tabular-nums font-semibold shrink-0 ${o.days == null || o.days >= 14 ? "text-[#FF6B7D]" : "text-[#B39548]"}`}>
                        {o.days == null ? "belum FU" : `${o.days} hari`}
                      </span>
                    </button>
                  );
                }}
              />

              <Section
                icon={Timer}
                tint="text-[#CE6968] bg-[#FDE7E9]"
                title="Proyek Overdue"
                empty="Tidak ada proyek yang lewat deadline."
                onMore={() => navigate("/notifications?type=project_overdue")}
                items={data.overdue_projects || []}
                renderItem={(o) => (
                  <button
                    key={o.id}
                    onClick={() => navigate("/projects")}
                    data-testid={`notif-project-overdue-${o.id}`}
                    className="w-full text-left px-5 py-3 hover:bg-[#F4F5F7] transition-colors border-b border-[#DCE0E7] last:border-0 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                      <Timer className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#090808] truncate flex items-baseline gap-1.5">
                        <span className="font-semibold">{o.customer}</span>
                        {o.service && <span className="text-[11px] text-[#A0A3A8] font-normal truncate">· {o.service}</span>}
                      </div>
                      <div className="text-xs text-[#A0A3A8] truncate">
                        {o.sales_name || "-"} · deadline {o.deadline}
                      </div>
                    </div>
                    <span className="text-xs tabular-nums font-bold shrink-0 text-[#CE6968]">
                      {o.days_overdue}h lewat
                    </span>
                  </button>
                )}
              />
              <Section
                icon={Receipt}
                tint="text-[#1E6B39] bg-[#C9F0D6]"
                title="Invoice Jatuh Tempo"
                empty="Tidak ada invoice yang mendekati jatuh tempo."
                items={data.invoices_due || []}
                onMore={() => navigate("/notifications?type=invoice_due")}
                renderItem={(iv) => (
                  <button
                    key={iv.id}
                    onClick={() => navigate(`/invoices?open=${iv.id}`)}
                    data-testid={`notif-invoice-due-${iv.id}`}
                    className="w-full text-left px-5 py-3 hover:bg-[#F4F5F7] transition-colors border-b border-[#DCE0E7] last:border-0 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#C9F0D6] text-[#1E6B39] flex items-center justify-center shrink-0">
                      <Receipt className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#090808] truncate font-semibold">{iv.number}</div>
                      <div className="text-xs text-[#A0A3A8] truncate">{iv.customer} · sisa {fmtIDR(iv.outstanding)}</div>
                    </div>
                    <span className={`text-xs tabular-nums font-bold shrink-0 ${iv.days < 0 ? "text-[#CE6968]" : "text-[#B39548]"}`}>
                      {iv.days < 0 ? `${Math.abs(iv.days)}h lewat` : iv.days === 0 ? "hari ini" : `H-${iv.days}`}
                    </span>
                  </button>
                )}
              />
            </>
          ) : (
            <Section
              icon={History}
              tint="text-[#626061] bg-[#DCE0E7]"
              title="Riwayat Catatan"
              empty="Belum ada riwayat catatan."
              items={historyNotes}
              renderItem={(n) => (
                <NoteRow
                  key={n.id}
                  n={n}
                  unread={false}
                  onOpen={() => openQuickForNote(n)}
                  onMarkUnread={(e) => markNoteUnread(e, n.id)}
                />
              )}
            />
          )}
        </div>

        <div className="px-4 py-3 border-t border-black/5 shrink-0 bg-white">
          <button
            onClick={() => navigate("/notifications")}
            data-testid="notif-see-all-btn"
            className="w-full h-10 rounded-full bg-[#090808] hover:bg-black text-white text-xs font-bold inline-flex items-center justify-center gap-1.5"
          >
            <Inbox className="w-3.5 h-3.5" /> Lihat semua notifikasi
          </button>
        </div>
      </PopoverContent>
    </Popover>
    <NotificationQuickView
      open={!!quickView}
      prospectId={quickView?.prospectId}
      focusReply={quickView?.focusReply}
      replyTo={quickView?.replyTo}
      onOpenChange={(o) => !o && setQuickView(null)}
      onAction={load}
    />
    </>
  );
}

function NoteRow({ n, unread, onOpen, onMarkRead, onMarkUnread }) {
  const rawNote = (n.note || "").trim();
  const truncated = rawNote.length > 140 ? rawNote.slice(0, 140).trimEnd() + "…" : rawNote;
  return (
    <div
      data-testid={`notif-note-${n.id}`}
      className={`group w-full px-5 py-3 hover:bg-[#F4F5F7] transition-colors border-b border-[#DCE0E7] last:border-0 flex items-start gap-3 ${
        unread ? "bg-[#F4F5F7]/60" : ""
      }`}
    >
      <button
        onClick={onOpen}
        className="flex items-start gap-3 flex-1 min-w-0 text-left"
      >
        <div className="shrink-0 mt-0.5">
          <Avatar name={n.actor_name} picture={n.actor_picture} size="sm" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-[#090808] truncate flex items-baseline gap-1.5">
            <span className="font-semibold">{n.customer}</span>
            {n.service && <span className="text-[11px] text-[#A0A3A8] font-normal truncate">· {n.service}</span>}
            {unread && <span className="inline-block w-2 h-2 rounded-full bg-[#090808] shrink-0" />}
          </div>
          {rawNote && (
            <div className={`text-xs italic mt-1 line-clamp-2 ${unread ? "text-[#626061]" : "text-[#A0A3A8]"}`}>
              &ldquo;{truncated}&rdquo;
            </div>
          )}
          <div className="text-[11px] text-[#A0A3A8] mt-1">{n.actor_nickname || n.actor_name} · {fmtDate(n.created_at)}</div>
        </div>
      </button>
      {unread ? (
        <button
          onClick={onMarkRead}
          data-testid={`mark-read-btn-${n.id}`}
          title="Tandai dibaca"
          className="shrink-0 w-7 h-7 rounded-full bg-white text-[#090808] hover:bg-[#090808] hover:text-white flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 ring-1 ring-violet-200"
        >
          <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      ) : (
        <button
          onClick={onMarkUnread}
          data-testid={`mark-unread-btn-${n.id}`}
          title="Kembalikan ke belum dibaca"
          className="shrink-0 w-7 h-7 rounded-full bg-white text-[#A0A3A8] hover:bg-[#F4F5F7] hover:text-[#090808] flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 ring-1 ring-[#DCE0E7]"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function Section({ icon: Icon, tint, title, empty, items, renderItem, limit = 6, onMore }) {
  return (
    <div className="py-1">
      <div className="px-5 pt-3 pb-2 flex items-center gap-2 sticky top-0 bg-white/60 backdrop-blur-sm z-10">
        <div className={`w-6 h-6 rounded-lg ${tint} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">{title}</div>
        <div className="ml-auto text-xs text-[#A0A3A8] font-num">{items.length}</div>
      </div>
      {items.length === 0 ? (
        <div className="mx-5 mt-1 mb-3 px-3 py-2.5 text-xs text-[#A0A3A8] italic bg-[#F4F5F7]/60 rounded-xl text-center">
          {empty}
        </div>
      ) : (
        <>
          {items.slice(0, limit).map(renderItem)}
          {items.length > limit && (
            <button
              onClick={onMore}
              data-testid={`notif-section-more-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="w-full px-5 py-2.5 text-[11px] font-bold text-[#3B4A63] hover:bg-[#F4F5F7] text-left"
            >
              +{items.length - limit} lainnya · lihat semua
            </button>
          )}
        </>
      )}
    </div>
  );
}
